import { useState, useEffect } from 'react';
import { Layout } from '../components/common/Layout';
import { bookService } from '../services/bookService';
import { bookRecordService } from '../services/bookRecordService';
import type { Book, BookRecord } from '../types';
import { BookCard } from '../components/public/BookCard';
import { searchBook } from '../utils/search';
import './BorrowedBooksPage.css';

interface BookWithStatus extends Book {
  isAvailable: boolean;
  borrowerRecord?: BookRecord;
}

export const BorrowedBooksPage = () => {
  const [allBooks, setAllBooks] = useState<BookWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    loadAllBooks();
  }, []);

  const loadAllBooks = async () => {
    try {
      setIsLoading(true);

      const [allBooksList, borrowedRecords] = await Promise.all([
        bookService.getAllBooks({ limit: 1000 }),
        bookRecordService.getAllRecords({ status: 'borrowed', limit: 1000 }),
      ]);

      const borrowedMap = new Map(borrowedRecords.map(record => [record.book_id, record]));

      const booksWithStatus: BookWithStatus[] = allBooksList.map(book => ({
        ...book,
        isAvailable: !borrowedMap.has(book.id),
        borrowerRecord: borrowedMap.get(book.id),
      }));

      const uniqueCategories = Array.from(
        new Set(booksWithStatus.map(book => book.category).filter(Boolean))
      ) as string[];
      setCategories(uniqueCategories);

      setAllBooks(booksWithStatus);
    } catch (err) {
      console.error('Error loading books:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredBooks = allBooks.filter((book) => {
    // Multi-way search: title, author, borrower name
    const borrowerName = book.borrowerRecord?.borrower_name;
    const matchesSearch = searchBook(searchTerm, book, borrowerName);
    const matchesCategory = !categoryFilter || book.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading books...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="borrowed-books-page">
        <div className="page-header-section">
          <h1>All Borrowed Books</h1>
          <p>View all books currently borrowed from the library</p>
        </div>

        <div className="filter-section">
          <div className="search-wrapper">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              className="form-input"
              placeholder="Search by title, author, or borrower name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="form-select category-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Total Count - Constant library statistics */}
        <div className="total-count total-count-top">
          <strong>Total:</strong> {allBooks.length} book{allBooks.length !== 1 ? 's' : ''} |
          <span className="available-count"> {allBooks.filter(b => b.isAvailable).length} Available</span> |
          <span className="borrowed-count"> {allBooks.filter(b => !b.isAvailable).length} Borrowed</span>
        </div>

        {filteredBooks.filter(b => !b.isAvailable).length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📖</div>
            <p className="empty-state-text">
              {searchTerm || categoryFilter
                ? 'No borrowed books match your search'
                : 'No books are currently borrowed'}
            </p>
          </div>
        ) : (
          <div className="books-grid">
            {filteredBooks
              .filter(book => !book.isAvailable)
              .map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  formatDate={formatDate}
                />
              ))}
          </div>
        )}
      </div>
    </Layout>
  );
};
